/*
 * Copyright (c) 2025, Gather
 * https://gather.life
 */

'use client';

import { type ReactNode, memo, useEffect } from 'react';

import { Card, Typography } from '@marketplace/gather-ui/components';

import { useAuthUserId } from '@/rest/hooks/use-auth/use-auth-user-id';
import { useAuthGetToken } from '@/rest/hooks/use-auth/use-auth-access-token';

import VehicleFilters, { VehicleFiltersSkeleton } from './vehicle-filters.view';
import { useCatalogFilterStore } from '../components/catalog-filter-store.provider';

type FiltersContainerProps = {
  searchHistoryId: string | null;
};

export function FiltersContainerSkeleton(): ReactNode {
  return <VehicleFiltersSkeleton />;
}

function FiltersContainer({ searchHistoryId }: FiltersContainerProps): ReactNode {
  const userId = useAuthUserId();
  const getToken = useAuthGetToken();

  const fetchFilters = useCatalogFilterStore((state) => state.fetchFilters);
  const fetchStatus = useCatalogFilterStore((state) => state.fetchState.status);
  const baseFiltersPopulated = useCatalogFilterStore((state) => state.meta.baseFiltersPopulated);

  useEffect(() => {
    if (searchHistoryId == null) {
      return;
    }

    if (userId == null) {
      fetchFilters(null, searchHistoryId);

      return;
    }

    (async () => {
      const token = await getToken();

      if (token == null) {
        fetchFilters(null, searchHistoryId);

        return;
      }

      fetchFilters({ userId, accessToken: token }, searchHistoryId);
    })();
  }, [fetchFilters, getToken, searchHistoryId, userId]);

  if (fetchStatus === 'error') {
    return (
      <Card sx={{ overflow: 'hidden' }}>
        <Typography variant='body'>There was an error loading the filter options</Typography>;
      </Card>
    );
  }

  if (!baseFiltersPopulated || fetchStatus === 'idle') {
    return <FiltersContainerSkeleton />;
  }

  return <VehicleFilters />;
}

export default memo(FiltersContainer);
